import { bindActionCreators } from "@reduxjs/toolkit"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { ArtInvestingExtraInfo, defaultArtInvestingExtraInfo, FileSrc } from "../../AssetContext"
import { actionCreators } from "../../state"
import { TitleColor1 } from "../typography/Typogtaphy"
import LabelAndInput from "./../LabelAndInput"
import HTMLVisualizer from "../utils/HTMLVisualizer"
import { UpdatableStringTable } from "../UpdatableTable"
import { OptimizedImage } from "../../functions/images"
import { sanitizeInputField } from "../../utils"
import ExtraInfoFormContainer from "./utils/ExtraInfoFormContainer"
import ExtraInfoFileUpload, { UploadUtils } from "./utils/ExtraInfoFileUpload"

export default function ExtraInfoArtInvestingForm(props: {
    assetInfo: any, setUpdateFun: any, media: any,
    uploadUtils: UploadUtils
}): JSX.Element {

    const dispatch = useDispatch()
    const actions = bindActionCreators(actionCreators, dispatch)

    // Asset ExtraInfo fields
    const [assetName, setAssetName] = useState<string>("")
    const [artworkName, setArtworkName] = useState<string>("")
    const [artistName, setArtistName] = useState<string>("")
    const [artLocation, setArtLocation] = useState<string[]>([])
    const [artSigned, setArtSigned] = useState<string>("")
    const [artTechnique, setArtTechnique] = useState<string>("")
    const [artSize, setArtSize] = useState<string>("")
    const [artYear, setArtYear] = useState<string>("")
    const [artEstimate, setArtEstimate] = useState<string>("")
    const [artInsurance, setArtInsurance] = useState<string>("")
    const [artOwners, setArtOwners] = useState<string[]>([])
    const [artDescr, setArtDescr] = useState<string>("")
    const [subtype, setSubtype] = useState<string>("")

    const [images2, setImages2] = useState<FileSrc[]>([])
    const [documents, setDocuments] = useState<FileSrc[]>([])
    const [coownership, setCoownership] = useState<FileSrc[]>([])
    const [prospectus, setProspectus] = useState<FileSrc[]>([])

    const [isApprovedAndTokenized, setIsApprovedAndTokenized] = useState<boolean>(false)
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false)

    var newArtInvestingInfo: ArtInvestingExtraInfo = {
        artist: sanitizeInputField(artistName),
        artwork_title: sanitizeInputField(artworkName),
        art_location: artLocation,
        signed: artSigned,
        technique: sanitizeInputField(artTechnique),
        size: sanitizeInputField(artSize),
        year: artYear,
        estimate: sanitizeInputField(artEstimate),
        insurance: sanitizeInputField(artInsurance),
        subtype: subtype,
        description: artDescr,
        owners: artOwners,
    }

    useEffect(() => {
        if (props.assetInfo.payload && props.assetInfo.payload.extra_info) {
            let artInvesting = defaultArtInvestingExtraInfo
            if (props.assetInfo.payload.extra_info.art_investing) {
                artInvesting = props.assetInfo.payload.extra_info.art_investing.sub_assets ?
                    props.assetInfo.payload.extra_info.art_investing.sub_assets[0] :
                    defaultArtInvestingExtraInfo
            }
            if (props.assetInfo.payload.asset_status_name !== "In Process" && props.assetInfo.payload.asset_status_name !== "Rejected")
                setIsApprovedAndTokenized(true)
            if (props.assetInfo.payload.asset_status_name === "Submitted")
                setIsSubmitted(true)

            setAssetName(props.assetInfo.payload.name)
            if (artInvesting) {
                setArtworkName(artInvesting.artwork_title)
                setArtistName(artInvesting.artist)
                setArtLocation(artInvesting.art_location)
                setArtSigned(artInvesting.signed)
                setArtTechnique(artInvesting.technique)
                setArtSize(artInvesting.size)
                setArtYear(artInvesting.year)
                setArtEstimate(artInvesting.estimate)
                setArtInsurance(artInvesting.insurance)
                setArtOwners(artInvesting.owners)
                setArtDescr(artInvesting.description)
                setSubtype(artInvesting.subtype)
            }
        }
    }, [props.assetInfo, props.assetInfo.loading])

    function updateEI() {
        var subAsset: any[] = []

        let tmp: string[] = []
        tmp = newArtInvestingInfo.owners.filter((el: string) => el !== "" && el !== undefined)
        newArtInvestingInfo.owners = tmp
        tmp = newArtInvestingInfo.art_location.filter((el: string) => el !== "" && el !== undefined)
        newArtInvestingInfo.art_location = tmp

        subAsset.push(newArtInvestingInfo)
        const assetExtraInfo = {
            art_investing: {
                sub_assets: subAsset
            }
        }
        return assetExtraInfo
    }

    useEffect(() => {
        props.setUpdateFun(updateEI)
    }, [assetName, artworkName, artistName, artLocation, artSigned, artTechnique, artSize,
        artYear, artEstimate, artInsurance, artOwners, artDescr, subtype])

    return <>
        <ExtraInfoFormContainer>
            <TitleColor1 className="col-span-12 text-lg">/ Artwork Information</TitleColor1>

            <LabelAndInput id={"assetName"} label={"Asset Name"} value={assetName} fun={setAssetName} type={"disabled"} className={"col-span-6"} required />
            <LabelAndInput id={"artworkTitle"} label={"Artwork Title *"} value={artworkName} fun={setArtworkName} type={isApprovedAndTokenized ? "disabled" : "text"} className={"col-span-6"} required />
            <LabelAndInput id={"description"} label={"Artwork Description * (text or HTML)"} value={artDescr} fun={setArtDescr} type={isApprovedAndTokenized ? "disabled" : "textarea"} rows={3} className="col-span-12" required />
            <HTMLVisualizer text={artDescr} className="w-full col-span-12 text-tertiary mb-5" />
            <LabelAndInput id={"artist"} label={"Artist *"} value={artistName} fun={setArtistName} type={isApprovedAndTokenized ? "disabled" : "text"} className={"col-span-6"} required />
            <LabelAndInput id={"signed"} label={"Signed *"} value={artSigned} fun={setArtSigned}
                type={isApprovedAndTokenized ? "disabled" : "select"} className={"col-span-6"} required options={[
                    { value: "", text: "Select" }, { value: "signed and archived", text: "Signed and Archived" },
                    { value: "yes", text: "Yes" }, { value: "no", text: "No" }
                ]} />
            <UpdatableStringTable title={"Locations"} state={artLocation} setState={setArtLocation}
                submitted={isSubmitted} className="mt-5" />
            <LabelAndInput id={"size"} label={"Size *"} value={artSize} fun={setArtSize} type={isApprovedAndTokenized ? "disabled" : "text"} className="col-span-6" required />
            <LabelAndInput id={"year"} label={"Year *"} value={artYear} fun={setArtYear} type={isApprovedAndTokenized ? "disabled" : "number"} className={"col-span-6"} required />
            <LabelAndInput id={"technique"} label={"Technique *"} value={artTechnique} fun={setArtTechnique} type={isApprovedAndTokenized ? "disabled" : "text"} className={"col-span-12"} required />
            <LabelAndInput id={"estimate"} label={"Estimate Value (EUR) *"} value={artEstimate} fun={setArtEstimate} type={isApprovedAndTokenized ? "disabled" : "number"} className={"col-span-6"} required />
            <LabelAndInput id={"insurance"} label={"Insurance *"} value={artInsurance} fun={setArtInsurance} type={isApprovedAndTokenized ? "disabled" : "text"} className={"col-span-6"} required />

            <UpdatableStringTable title={"Owners"} state={artOwners} setState={setArtOwners}
                submitted={isSubmitted} className="mt-5" />

            <ExtraInfoFileUpload assetName={assetName} isSubmitted={isSubmitted}
                coownership={coownership} setCoownership={setCoownership}
                prospectus={prospectus} setProspectus={setProspectus}
                images2={images2} setImages2={setImages2}
                documents={documents} setDocuments={setDocuments}
                uploadUtils={props.uploadUtils} media={props.media} />
        </ExtraInfoFormContainer>
    </>
}